import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import * as saleService from '../../services/saleService';
import { Sale, SaleCreate, SaleItemCreate, PaymentMethod } from '../../types/sale';
import { Item } from '../../types/item';
import { indexedDBService } from '../../services/indexedDBService';

export interface CartItem {
  item: Item;
  qty: number;
  price: number;
  discount: number;
}

interface CashierState {
  cartItems: CartItem[];
  subtotal: number;
  discount: number;
  discount_percentage?: number;
  total: number;
  currentSale: Sale | null;
  recentSales: Sale[];
  loading: boolean;
  error: string | null;
  isOfflineMode: boolean;
  pendingSyncCount: number;
}

const initialState: CashierState = {
  cartItems: [],
  subtotal: 0,
  discount: 0,
  discount_percentage: undefined,
  total: 0,
  currentSale: null,
  recentSales: [],
  loading: false,
  error: null,
  isOfflineMode: false,
  pendingSyncCount: 0,
};

// Create sale with offline support
export const createSaleAsync = createAsyncThunk(
  'cashier/createSale',
  async ({ saleData, user, branchId }: { saleData: SaleCreate; user: any; branchId?: number }, { rejectWithValue }) => {
    try {
      // OWNER must use branch-specific endpoint
      let response;
      if (user?.role === 'owner' && branchId) {
        response = await saleService.createSaleForBranch(branchId, saleData);
      } else {
        response = await saleService.createSale(saleData);
      }
      return { sale: response, offline: false };
    } catch (error: any) {
      // Check if it's a REAL network error (offline) - not validation errors like 422
      const isNetworkError = !navigator.onLine || 
                             error.code === 'ERR_NETWORK' || 
                             error.message?.includes('Network Error') ||
                             error.message?.includes('Failed to fetch');
      
      // Don't treat HTTP errors (400, 422, 500, etc.) as offline - they're server responses
      const isServerError = error.response?.status !== undefined;
      
      if (isNetworkError && !isServerError) {
        // Save to IndexedDB for later sync
        try {
          const offlineId = await indexedDBService.savePendingSale(saleData);
          console.log('💾 Sale saved offline:', offlineId);
          
          // Return mock sale object for UI
          return {
            sale: {
              id: -1,
              invoice_no: `OFFLINE-${Date.now()}`,
              ...saleData,
              created_at: new Date().toISOString(),
              status: 'pending_sync'
            } as any,
            offline: true
          };
        } catch (dbError) {
          return rejectWithValue('Failed to save offline sale');
        }
      }
      
      // Server error - return the actual error message
      return rejectWithValue(error.response?.data?.detail || error.message || 'Failed to create sale');
    }
  }
);

export const checkDiscountEligibilityAsync = createAsyncThunk(
  'cashier/checkDiscount',
  async (data: { customer_id?: number; items: SaleItemCreate[] }) => {
    const response = await saleService.checkDiscountEligibility(data);
    return response;
  }
);

export const fetchRecentSalesAsync = createAsyncThunk(
  'cashier/fetchRecentSales',
  async () => {
    const response = await saleService.getSales({ page: 1, page_size: 10 });
    return response;
  }
);

const cashierSlice = createSlice({
  name: 'cashier',
  initialState,
  reducers: {
    addToCart(state, action: PayloadAction<{ item: Item; qty?: number }>) {
      const { item, qty = 1 } = action.payload;
      const existingItem = state.cartItems.find(ci => ci.item.id === item.id);
      
      if (existingItem) {
        existingItem.qty += qty;
      } else {
        state.cartItems.push({
          item,
          qty,
          price: parseFloat(item.sell_price.toString()) || 0,
          discount: 0,
        });
      }
      
      // Save cart to IndexedDB for offline persistence (if initialized)
      if (indexedDBService.isInitialized()) {
        indexedDBService.saveOfflineCart(state.cartItems).catch(() => {});
      }
      
      // Recalculate totals
      cashierSlice.caseReducers.calculateTotals(state);
    },
    
    removeFromCart(state, action: PayloadAction<number>) {
      state.cartItems = state.cartItems.filter(ci => ci.item.id !== action.payload);
      cashierSlice.caseReducers.calculateTotals(state);
    },
    
    updateCartItemQty(state, action: PayloadAction<{ itemId: number; qty: number }>) {
      const item = state.cartItems.find(ci => ci.item.id === action.payload.itemId);
      if (item) {
        item.qty = action.payload.qty;
        cashierSlice.caseReducers.calculateTotals(state);
      }
    },
    
    updateCartItemDiscount(state, action: PayloadAction<{ itemId: number; discount: number }>) {
      const item = state.cartItems.find(ci => ci.item.id === action.payload.itemId);
      if (item) {
        item.discount = action.payload.discount;
        cashierSlice.caseReducers.calculateTotals(state);
      }
    },
    
    applyDiscount(state, action: PayloadAction<{ discount?: number; percentage?: number }>) {
      state.discount = action.payload.discount || 0;
      state.discount_percentage = action.payload.percentage;
      cashierSlice.caseReducers.calculateTotals(state);
    },
    
    clearCart(state) {
      state.cartItems = [];
      state.subtotal = 0;
      state.discount = 0;
      state.discount_percentage = undefined;
      state.total = 0;
      state.currentSale = null;
      state.error = null;
      
      // Clear offline cart
      indexedDBService.clearOfflineCart().catch(console.error);
    },
    
    setOfflineMode(state, action: PayloadAction<boolean>) {
      state.isOfflineMode = action.payload;
    },
    
    setPendingSyncCount(state, action: PayloadAction<number>) {
      state.pendingSyncCount = action.payload;
    },
    
    restoreCartFromOffline(state, action: PayloadAction<CartItem[]>) {
      state.cartItems = action.payload;
      cashierSlice.caseReducers.calculateTotals(state);
    },
    
    calculateTotals(state) {
      state.subtotal = state.cartItems.reduce(
        (sum, item) => sum + (item.price * item.qty - item.discount),
        0
      );
      
      // Apply percentage discount if set
      if (state.discount_percentage) {
        state.discount = state.subtotal * (state.discount_percentage / 100);
      }
      
      state.total = state.subtotal - state.discount;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createSaleAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createSaleAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.currentSale = action.payload.sale;
        
        if (action.payload.offline) {
          state.isOfflineMode = true;
          state.error = 'Sale saved offline. Will sync when connection is restored.';
        }
        
        state.cartItems = [];
        state.subtotal = 0;
        state.discount = 0;
        state.discount_percentage = undefined;
        state.total = 0;
        
        // Clear cart from IndexedDB (if initialized)
        if (indexedDBService.isInitialized()) {
          indexedDBService.clearOfflineCart().catch(() => {});
        }
      })
      .addCase(createSaleAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || 'Failed to create sale';
      })
      .addCase(fetchRecentSalesAsync.fulfilled, (state, action) => {
        state.recentSales = action.payload as any;
      });
  },
});

export const {
  addToCart,
  removeFromCart,
  updateCartItemQty,
  updateCartItemDiscount,
  applyDiscount,
  clearCart,
  calculateTotals,
  setOfflineMode,
  setPendingSyncCount,
  restoreCartFromOffline,
} = cashierSlice.actions;

export default cashierSlice.reducer;
